import CallError from '../../../utils/CallError';
import { getBadParams, getMissingKeys } from '../../../utils/general';
import { reviewSettings } from '../setup';

/**
 * @typedef ReviewObject
 * @type {Object}
 * @property {string} assetId
 * @property {{version: string}} assetMetadata
 * @property {string} assetType
 * @property {string} authorName
 * @property {string} createdAt
 * @property {string} id
 * @property {bool} isEditable
 * @property {string} lastEditedAt
 * @property {number} rating
 * @property {number} replyCount
 * @property {string} title
 * @property {string} text
 */

/**
 * Gets the review provided by the user on a given asset
 * @param {object} params - an object of params
 * @param {string} params.accessToken - IMS access token
 * @param {string} params.imsUserId - User's IMS userId
 * @param {string} params.assetId - ID of the plugin you want the review for
 * @param {AbortSignal} [signal] - Abort signal that can be used to abort the fetch request
 * @returns {Promise<ReviewObject>}
 * @throws {CallError}
 */
export default async (params = {}, signal = null) => {
  const requiredParams = ['accessToken', 'imsUserId', 'assetId'];
  const paramsRules = {
    accessToken: { allowedTypes: ['string'] },
    imsUserId: { allowedTypes: ['string'] },
    assetId: { allowedTypes: ['string'] },
  };

  if (!reviewSettings.endpoint['get-user-review']) {
    throw new CallError('params', {}, 400, { message: 'Missing value for RNR get-my-review endpoint' });
  }
  if (!reviewSettings.apiKey) {
    throw new CallError('params', {}, 400, { message: 'Missing value for RNR api key' });
  }

  const missingParams = getMissingKeys(params, requiredParams);
  let badParams = [];
  if (Object.keys(params).length) {
    badParams = getBadParams(params, paramsRules);
  }
  if (missingParams.length || badParams.length) {
    throw new CallError('params', null, 404, { missingParams, badParams });
  }

  const { accessToken, imsUserId, assetId } = params;

  const searchParams = new URLSearchParams({
    assetId,
    assetType: 'PLUGIN',
  });
  searchParams.sort();
  const response = await fetch(`${reviewSettings.endpoint['get-user-review']}?${searchParams.toString()}`, {
    signal,
    method: 'GET',
    headers: {
      accept: 'application/vnd.adobe-review.user-review-data-detailed-v1+json',
      authorization: accessToken,
      'x-api-key': reviewSettings.apiKey,
      'x-gw-ims-user-id': imsUserId,
    },
  });
  /**
   * @type {ReviewObject}
   */
  let content = await response.text();
  try {
    content = JSON.parse(content);
  } catch (e) {
    // response is not json
  }

  if (!response.ok) {
    throw new CallError('http', content, response.status, { message: response.statusText });
  } else {
    return content;
  }
};
